import { Link } from '@tanstack/react-router';
import {
  HeroControl,
  Menu,
  MenuContent,
  MenuGroup,
  MenuGroupLabel,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
} from '@wundo/ui';
import { ChevronRight, FileText } from 'lucide-react';
import type { MeetingPageTemplate } from '@wundo/shared/meetingPage.ts';
import type { MeetingTypeOption } from '@/lib/withCurrentTemplate.ts';
import { MeetingTypeRow } from './MeetingTypeRow.tsx';
import { m } from '@/paraglide/messages.js';

export interface MeetingTypeControlProps {
  /** The gesprekstype this conversation follows, or null when none resolved. */
  current: MeetingPageTemplate | null;
  /** The types this meeting's kind may follow; any kind on a manual meeting. */
  templates: MeetingTypeOption[];
  onOpen: () => void;
  onPick: (template: MeetingTypeOption) => void;
}

/**
 * What kind of conversation this is, in the row under the hero, and the way to
 * change it. Going back to the default is picking the default type.
 *
 * The list is fetched when the menu is first opened: most visits never touch it.
 * The way to the templates sits at the foot, because "this one is not right
 * either" is exactly what somebody reading the list is thinking.
 */
export function MeetingTypeControl({
  current,
  templates,
  onOpen,
  onPick,
}: MeetingTypeControlProps) {
  return (
    <Menu>
      <MenuTrigger
        render={
          <HeroControl
            icon={<FileText aria-hidden='true' />}
            label={m.meeting_type_label()}
            chevron
          />
        }
        onClick={onOpen}
      >
        {current?.name ?? m.meeting_type_none()}
      </MenuTrigger>
      {/* Flush, because the type that is on is drawn as a filled row. */}
      <MenuContent flush>
        <MenuGroup>
          <MenuGroupLabel>{m.meeting_type_menu_title()}</MenuGroupLabel>
          {templates.length === 0 && (
            <MenuItem disabled>{m.meeting_prep_type_empty()}</MenuItem>
          )}
          {templates.map((template) => (
            <MeetingTypeRow
              key={template.id}
              on={template.id === current?.id}
              color={template.color}
              onPick={() => onPick(template)}
            >
              {template.name}
            </MeetingTypeRow>
          ))}
        </MenuGroup>
        <MenuSeparator />
        <MenuItem render={<Link to='/settings/templates' />}>
          <FileText aria-hidden='true' />
          {m.meeting_type_manage()}
          <ChevronRight aria-hidden='true' className='ml-auto' />
        </MenuItem>
      </MenuContent>
    </Menu>
  );
}
